import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { authAPI } from "../services/authAPI";
import { formatAPIError } from "../utils/validation";
import Header from "../components/layout/Header";
import "./styles/AccountUnlock.css";

const AccountUnlock = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [token, setToken] = useState(null);
  const [tokenChecked, setTokenChecked] = useState(false);

  const [formData, setFormData] = useState({ answer1: "", answer2: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const tokenFromURL = searchParams.get("token");
    if (tokenFromURL) {
      setToken(tokenFromURL);
    } else {
      setError("Invalid unlock link. Please check your email for the correct link.");
    }
    setTokenChecked(true);
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (error) setError("");
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.answer1.trim()) newErrors.answer1 = "Security answer 1 is required";
    if (!formData.answer2.trim()) newErrors.answer2 = "Security answer 2 is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const answers = [formData.answer1.trim(), formData.answer2.trim()];
      await authAPI.unlockAccount({ token, answers });

      setSuccess(
        "Security answers verified! A password reset link has been sent to your email. Please check your inbox."
      );

      setFormData({ answer1: "", answer2: "" });

      setTimeout(() => {
        navigate("/login", {
          state: {
            message: "Password reset link sent. Check your email to complete account unlock.",
            type: "success",
          },
        });
      }, 3000);
    } catch (err) {
      const errorMessage = formatAPIError(err);

      if (errorMessage.includes("Security answers incorrect")) {
        setError(
          "Security answers are incorrect. Please try again with the exact answers you provided during registration."
        );
      } else if (errorMessage.includes("Invalid or expired unlock link")) {
        setError("This unlock link is invalid or expired. Please request a new unlock link.");
      } else {
        setError(`Failed to unlock account: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading while we check token
  if (!tokenChecked) {
    return (
      <>
        <Header />
        <div className="au-page">
          <div className="au-card">
            <div className="au-body">
              <div className="au-spinner" />
              <p className="au-muted">Verifying unlock link…</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Invalid token state
  if (!token) {
    return (
      <>
        <Header />
        <div className="au-page">
          <div className="au-card">
            <div className="au-top">
              <div className="au-icon">
                {/* lock icon */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M7 10V8a5 5 0 0 1 10 0v2"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M7 10h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
                    stroke="#2563EB"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <h1 className="au-title">Unlock Account</h1>
              <p className="au-subtitle">The unlock link is missing or invalid</p>
            </div>

            <div className="au-body">
              {error && <div className="au-alert au-alert--error">{error}</div>}

              <div className="au-footer">
                <Link className="au-backlink" to="/login">
                  <span className="au-backicon">←</span>
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Normal form state
  return (
    <>
      <Header />

      <div className="au-page">
        <div className="au-card">
          <div className="au-top">
            <div className="au-icon">
              {/* unlock icon variant */}
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M7 10V8a5 5 0 0 1 9.5-2"
                  stroke="#2563EB"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M7 10h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
                  stroke="#2563EB"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h1 className="au-title">Unlock Account</h1>
            <p className="au-subtitle">Please answer your security questions to unlock your account</p>
          </div>

          <div className="au-body">
            {error && <div className="au-alert au-alert--error">{error}</div>}
            {success && <div className="au-alert au-alert--success">{success}</div>}

            <form onSubmit={handleSubmit} className="au-form">
              <div className="au-field">
                <label className="au-label">Security Answer 1</label>
                <input
                  className={errors.answer1 ? "au-input au-input--error" : "au-input"}
                  name="answer1"
                  value={formData.answer1}
                  onChange={handleInputChange}
                  placeholder="Enter your answer"
                  disabled={loading}
                />
                {errors.answer1 && <div className="au-error">{errors.answer1}</div>}
              </div>

              <div className="au-field">
                <label className="au-label">Security Answer 2</label>
                <input
                  className={errors.answer2 ? "au-input au-input--error" : "au-input"}
                  name="answer2"
                  value={formData.answer2}
                  onChange={handleInputChange}
                  placeholder="Enter your answer"
                  disabled={loading}
                />
                {errors.answer2 && <div className="au-error">{errors.answer2}</div>}
              </div>

              <button className="au-btn au-btn--primary" type="submit" disabled={loading}>
                {loading ? "Verifying..." : "Unlock Account"}
              </button>

              <div className="au-help">
                <span className="au-muted">Can't remember your security answers?</span>{" "}
                <Link to="/support" className="au-link">
                  Contact Support
                </Link>
              </div>

              <div className="au-footer">
                <Link className="au-backlink" to="/login">
                  <span className="au-backicon">←</span>
                  Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountUnlock;
